// src/components/GarbageAlert/GarbageAlertForm.js
// Simple, user-friendly garbage truck alert setup

import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { API, APP_CONSTANTS } from '../../utils/config';
import MapController from '../Map/MapController';

// Create home marker icon
const createHomeIcon = () => {
  return L.divIcon({
    html: `<div style="
      font-size: 32px;
      text-align: center;
      filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
    ">🏠</div>`,
    className: 'home-marker-icon',
    iconSize: [36, 36],
    iconAnchor: [18, 18],
  });
};

// Map click handler component
const MapClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click: (e) => onMapClick(e),
  });
  return null;
};

const GarbageAlertForm = ({ onClose, onSuccess, existingAlert = null }) => {
  // Form state
  const [step, setStep] = useState(1);
  const [homeLocation, setHomeLocation] = useState(null);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [alertDistance, setAlertDistance] = useState(500); // feet
  const [notifyMinutesBefore, setNotifyMinutesBefore] = useState(10);
  const [selectedDays, setSelectedDays] = useState(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
  const [alertName, setAlertName] = useState('');

  // Data state
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [mapCenter, setMapCenter] = useState(APP_CONSTANTS.MAP.BELIZE_CENTER);

  const allDays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

  // Load routes on mount
  useEffect(() => {
    fetchRoutes();
    loadSavedHomeLocation();
  }, []);

  // Load existing alert data for editing
  useEffect(() => {
    if (existingAlert) {
      setAlertName(existingAlert.name || '');
      setHomeLocation(existingAlert.home_location);
      setSelectedRoute(existingAlert.route_id);
      setAlertDistance(existingAlert.alert_distance_feet || 500);
      setNotifyMinutesBefore(existingAlert.notify_minutes_before || 10);
      setSelectedDays(existingAlert.notification_days || ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']);
      if (existingAlert.home_location) {
        setMapCenter([existingAlert.home_location.lat, existingAlert.home_location.lng]);
      }
    }
  }, [existingAlert]);

  const fetchRoutes = async () => {
    try {
      const response = await fetch(`${API}/van-routes/active`);
      if (response.ok) {
        const data = await response.json();
        setRoutes(data);
        // Auto-select if only one route
        if (data.length === 1) {
          setSelectedRoute(data[0].id);
        }
      }
    } catch (err) {
      console.error('Error fetching routes:', err);
    }
  };

  const loadSavedHomeLocation = () => {
    const saved = localStorage.getItem('myHomeLocation');
    if (saved) {
      try {
        const location = JSON.parse(saved);
        setHomeLocation(location);
        setMapCenter([location.lat, location.lng]);
      } catch (e) {
        console.error('Error loading home location:', e);
      }
    }
  };

  const handleMapClick = (e) => {
    const { lat, lng } = e.latlng;
    const newLocation = { lat, lng };
    setHomeLocation(newLocation);
    // Save to localStorage for reuse
    localStorage.setItem('myHomeLocation', JSON.stringify(newLocation));
  };

  const handleUseCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          setHomeLocation(newLocation);
          setMapCenter([newLocation.lat, newLocation.lng]);
          localStorage.setItem('myHomeLocation', JSON.stringify(newLocation));
        },
        (error) => {
          setError('Could not get your location. Please click on the map instead.');
        }
      );
    }
  };

  const toggleDay = (day) => {
    if (selectedDays.includes(day)) {
      setSelectedDays(selectedDays.filter(d => d !== day));
    } else {
      setSelectedDays([...selectedDays, day]);
    }
  };

  const handleSubmit = async () => {
    if (!homeLocation) {
      setError('Please set your home location');
      return;
    }
    if (!selectedRoute) {
      setError('Please select a garbage route');
      return;
    }
    if (selectedDays.length === 0) {
      setError('Please select at least one day');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('token') || sessionStorage.getItem('token');

      const alertData = {
        name: alertName || `My Garbage Alert`,
        route_id: selectedRoute,
        home_location: homeLocation,
        alert_distance_feet: alertDistance,
        notify_minutes_before: notifyMinutesBefore,
        notification_days: selectedDays,
        is_active: true
      };

      const url = existingAlert
        ? `${API}/garbage-alerts/${existingAlert.id}`
        : `${API}/garbage-alerts`;

      const method = existingAlert ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(alertData)
      });

      if (response.ok) {
        onSuccess && onSuccess();
        onClose && onClose();
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to save alert');
      }
    } catch (err) {
      setError('Failed to save alert. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getSelectedRouteName = () => {
    const route = routes.find(r => r.id === selectedRoute);
    return route ? route.name : 'Select a route';
  };

  // Convert feet to meters for map circle
  const alertRadiusMeters = alertDistance * 0.3048;

  return (
    <div className="bg-white rounded-xl shadow-xl overflow-hidden max-w-lg mx-auto">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 text-white p-4">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-xl font-bold flex items-center">
              <span className="mr-2">🔔</span>
              {existingAlert ? 'Edit Alert' : 'Create Garbage Alert'}
            </h2>
            <p className="text-green-100 text-sm mt-1">
              Get notified when the truck is near your home
            </p>
          </div>
          {onClose && (
            <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-full">
              <span className="text-xl">✕</span>
            </button>
          )}
        </div>

        {/* Progress Steps */}
        <div className="flex mt-4 space-x-2">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`flex-1 h-2 rounded-full ${step >= s ? 'bg-white' : 'bg-white/30'}`}
            />
          ))}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="mx-4 mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Step 1: Set Home Location */}
      {step === 1 && (
        <div className="p-4">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center">
            <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">1</span>
            Where is your home?
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Tap the map or use your current location to set where you want to be notified.
          </p>

          <button
            onClick={handleUseCurrentLocation}
            className="w-full mb-3 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center font-medium"
          >
            <span className="mr-2">📍</span>
            Use My Current Location
          </button>

          <div className="relative h-64 rounded-lg overflow-hidden border-2 border-gray-200">
            <MapContainer
              center={mapCenter}
              zoom={15}
              style={{ height: '100%', width: '100%' }}
            >
              <MapController center={mapCenter} zoom={15} />
              <MapClickHandler onMapClick={handleMapClick} />
              <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

              {homeLocation && (
                <>
                  <Marker
                    position={[homeLocation.lat, homeLocation.lng]}
                    icon={createHomeIcon()}
                  />
                  <Circle
                    center={[homeLocation.lat, homeLocation.lng]}
                    radius={alertRadiusMeters}
                    pathOptions={{
                      color: '#10b981',
                      fillColor: '#6ee7b7',
                      fillOpacity: 0.2,
                      weight: 2
                    }}
                  />
                </>
              )}
            </MapContainer>

            {!homeLocation && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/20 pointer-events-none">
                <div className="bg-white px-4 py-2 rounded-lg shadow text-gray-700 font-medium">
                  👆 Tap to set your home
                </div>
              </div>
            )}
          </div>

          {homeLocation && (
            <p className="mt-2 text-sm text-green-600 flex items-center">
              <span className="mr-1">✓</span>
              Location set! The circle shows your alert area.
            </p>
          )}
        </div>
      )}

      {/* Step 2: Choose Route and Distance */}
      {step === 2 && (
        <div className="p-4">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center">
            <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">2</span>
            Alert Settings
          </h3>

          {/* Route Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Garbage Route
            </label>
            {routes.length === 1 ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg">
                <span className="font-medium text-green-800">🚛 {routes[0].name} Route</span>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {routes.map((route) => (
                  <button
                    key={route.id}
                    onClick={() => setSelectedRoute(route.id)}
                    className={`p-3 rounded-lg border-2 text-left transition-colors ${
                      selectedRoute === route.id
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <span className="font-medium">🚛 {route.name}</span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Alert Distance */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Alert me when truck is within:
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[300, 500, 1000].map((dist) => (
                <button
                  key={dist}
                  onClick={() => setAlertDistance(dist)}
                  className={`p-3 rounded-lg border-2 text-center transition-colors ${
                    alertDistance === dist
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-green-300'
                  }`}
                >
                  <span className="font-bold">{dist}</span>
                  <span className="text-sm block text-gray-500">feet</span>
                </button>
              ))}
            </div>
          </div>

          {/* ETA Info Box */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-2">
            <div className="flex items-start">
              <span className="text-blue-600 mr-2">ℹ️</span>
              <div className="text-sm text-blue-800">
                <p className="font-medium">How it works:</p>
                <ul className="mt-1 space-y-1 text-blue-700">
                  <li>• You'll get <strong>one notification per day</strong> when the truck enters your alert zone</li>
                  <li>• The notification includes an <strong>estimated arrival time (ETA)</strong> based on the truck's current speed</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Step 3: Choose Days and Name */}
      {step === 3 && (
        <div className="p-4">
          <h3 className="font-bold text-gray-800 mb-2 flex items-center">
            <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm mr-2">3</span>
            When to Alert
          </h3>

          {/* Alert Name */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Name this alert (optional)
            </label>
            <input
              type="text"
              value={alertName}
              onChange={(e) => setAlertName(e.target.value)}
              placeholder="e.g., My Home"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>

          {/* Day Selection */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Which days should we alert you?
            </label>
            <div className="flex flex-wrap gap-2">
              {allDays.map((day) => (
                <button
                  key={day}
                  onClick={() => toggleDay(day)}
                  className={`px-4 py-2 rounded-full font-medium transition-colors ${
                    selectedDays.includes(day)
                      ? 'bg-green-600 text-white'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {day}
                </button>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gray-50 rounded-lg p-4 mt-4">
            <h4 className="font-medium text-gray-800 mb-2">Alert Summary</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>📍 Location: Set ✓</li>
              <li>🚛 Route: {getSelectedRouteName()}</li>
              <li>📏 Alert zone: {alertDistance} feet radius</li>
              <li>📅 Days: {selectedDays.join(', ') || 'None selected'}</li>
              <li>🔔 One notification per day with ETA</li>
            </ul>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="p-4 bg-gray-50 border-t flex justify-between">
        {step > 1 ? (
          <button
            onClick={() => setStep(step - 1)}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            ← Back
          </button>
        ) : (
          <button
            onClick={onClose}
            className="px-6 py-2 text-gray-600 hover:text-gray-800 font-medium"
          >
            Cancel
          </button>
        )}

        {step < 3 ? (
          <button
            onClick={() => {
              if (step === 1 && !homeLocation) {
                setError('Please set your home location first');
                return;
              }
              if (step === 2 && !selectedRoute) {
                setError('Please select a route');
                return;
              }
              setError(null);
              setStep(step + 1);
            }}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={loading}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium disabled:bg-gray-400"
          >
            {loading ? 'Saving...' : (existingAlert ? 'Update Alert' : 'Create Alert')}
          </button>
        )}
      </div>
    </div>
  );
};

export default GarbageAlertForm;
